import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { ArticlesService, CustomersService, RateArticleCommand, CustomerRatingDto, CreateFavoriteArticleCommand, DeleteFavoriteArticleCommand } from 'src/api';


interface RatingState {
  tempRating: number | null;
  initialRating: number | null;
  status: 'none' | 'loading' | 'saved' | 'editing' | 'error';
}

const initialRatingState: RatingState = {
  tempRating: null,
  initialRating: null,
  status: 'none',
};

interface UserRatingFavorite {
  favorites: number[];
  ratings: CustomerRatingDto[];
}

const initialUserSettings: UserRatingFavorite = {
  favorites: [],
  ratings: [],
};


@Injectable({
  providedIn: 'root'
})
export class ArticleRatingFavoriteService {
  private ratingState: BehaviorSubject<RatingState> = new BehaviorSubject<RatingState>(initialRatingState);

  ratingState$ = this.ratingState.asObservable();
  ratingTemp$ = this.ratingState$.pipe(map((state) => state.tempRating));
  ratingInitial$ = this.ratingState$.pipe(map((state) => state.initialRating));

  private userSettingsState: BehaviorSubject<UserRatingFavorite> = new BehaviorSubject<UserRatingFavorite>(
    initialUserSettings
  );
  userFavoriteRating$ = this.userSettingsState.asObservable();

  userFavorites$ = this.userFavoriteRating$.pipe(map((settings) => settings.favorites));
  userRatings$ = this.userFavoriteRating$.pipe(map((settings) => settings.ratings));

  constructor(
    private ratingService: ArticlesService,
    private customersServiceApi: CustomersService
  ) { }

  get ratingValues() {
    return this.ratingState.value;
  }

  get userSettings() {
    return this.userSettingsState.value;
  }

  fetchCustomerRatings() {
    this.customersServiceApi.apiCustomersMeRatingsGet().subscribe((res) => {
      this.userSettingsState.next({ ...this.userSettings, ratings: res });
    });
  }

  fetchCustomerFavorites() {
    this.customersServiceApi.apiCustomersMeFavoritesGet().subscribe((res) => {
      this.userSettingsState.next({ ...this.userSettings, favorites: res });
    });
  }

  setTempRating(rating: number) {
    this.ratingState.next({ ...this.ratingValues, tempRating: rating });
  }

  setInitialRating(rating: number) {
    this.ratingState.next({ ...this.ratingValues, initialRating: rating });
  }

  saveArticleRating(rating: number, articleId: number, shopId: number) {
    const command: RateArticleCommand = {
      articleId,
      azureAdUpn: '',
      value: rating,
      shopId
    };

    return this.ratingService.apiArticlesRatePut(command).pipe(tap(() => this.updateCustomerRating(articleId, rating)));
  }

  clearArticleRating() {
    this.ratingState.next(initialRatingState);
  }

  /** get the Customer Rating for the article informed
   *
   * @param articleId id of the article
   * @returns CustomerRatingDto with ArticleId and Rating - if no rating, return undefined
   */
  getCustomerArticleRating(articleId: number): CustomerRatingDto | undefined {
    return this.userSettings.ratings.find((rating) => rating.articleId === articleId);
  }

  postArticleAsFavorite(articleId: number) {
    const command: CreateFavoriteArticleCommand = {
      articleId,
      azureAdUpn: '',
    };

    return this.customersServiceApi.apiCustomersMeFavoritearticlePost(command).pipe(
      tap(() => {
        this.addRemoveCustomerFavorite(articleId);
      })
    );
  }

  deleteArticleAsFavorite(articleId: number) {
    const command: DeleteFavoriteArticleCommand = {
      articleId,
      azureAdUpn: '',
    };
    return this.customersServiceApi.apiCustomersMeFavoritearticleDelete(command).pipe(
      tap(() => {
        this.addRemoveCustomerFavorite(articleId, false);
      })
    );
  }

  private addRemoveCustomerFavorite(articleId: number, isAdd: boolean = true) {
    const currFavorites = [...this.userSettings.favorites];

    const indexFavorite = currFavorites.indexOf(articleId);

    if (isAdd && indexFavorite == -1) {
      currFavorites.push(articleId);
    } else if (!isAdd && indexFavorite >= 0) {
      currFavorites.splice(indexFavorite, 1);
    }

    this.userSettingsState.next({ ...this.userSettings, favorites: currFavorites });
  }

  private updateCustomerRating(articleId: number, rating: number) {
    const currRatings = [...this.userSettings.ratings];

    const indexRating = currRatings.findIndex((rating) => rating.articleId === articleId);

    if (indexRating === -1) {
      const newRating: CustomerRatingDto = {
        articleId,
        rating,
      };
      currRatings.push(newRating);
    } else {
      currRatings[indexRating].rating = rating;
    }
    this.userSettingsState.next({ ...this.userSettings, ratings: currRatings });
  }
}
